/**
 * Analytics Constants
 *
 * Centralized constants for analytics tracking.
 * Co-located with analytics utilities for easier imports.
 */

/**
 * Analytics event trigger types
 * Used for programmatic tracking with trackFromElement()
 */
export const ANALYTICS_EVENT_TRIGGERS = {
  CLICK: 'click',
  IMPRESSION: 'impression',
  MODAL_OPEN: 'modalOpen',
  MODAL_CLOSE: 'modalClose',
  MODAL_NAVIGATE: 'modalNavigate',
  CUSTOM_EVENT: 'custom_event',
  HOVER_EVENT: 'hover_event',
  ACCORDION_CLICK: 'accordion_click',
} as const;

/**
 * Analytics event names (for event payload)
 * Centralized constant for all GA4 event types
 *
 * @example
 * ```typescript
 * import { EVENT_NAMES } from '@marketing-web/ui';
 *
 * const analyticsConfig = {
 *   event: EVENT_NAMES.LOAD_MORE_CLICK,
 *   moduleName: 'blog-list',
 *   componentFamily: 'blog',
 * };
 * ```
 */
export const ANALYTICS_EVENT_NAMES = {
  // Click Events
  CLICK: 'click',
  CTA_CLICK: 'cta_click',
  CARD_CLICK: 'card_click',
  LIST_ITEM_CLICK: 'list_item_click',
  LOAD_MORE_CLICK: 'load_more_click',
  PAGINATION_CLICK: 'pagination_click',
  FILTER_CLICK: 'filter_click',
  ACCORDION_CLICK: 'accordion_click',
  TAB_CLICK: 'tab_click',
  SIDE_NAV_CLICK: 'side_nav_click',
  DROPDOWN_TOGGLE: 'dropdown_toggle',
  APPLY_NOW_CLICK: 'apply_now_click',
  VIEW_JOB_DESCRIPTION_CLICK: 'view_job_desc_click',
  JOIN_NOW_CLICK: 'join_now_click',

  // Download Events
  RESOURCE_DOWNLOAD: 'resource_download',

  // Impression Events
  MODULE_IMPRESSION: 'module_impression',

  // Modal Events
  MODAL_OPEN: 'modal_open',
  MODAL_CLOSE: 'modal_close',
  MODAL_NAVIGATE: 'modal_navigate',

  // Menu Events
  MENU_OPEN: 'menu_open',
  MENU_CLOSE: 'menu_close',

  // Carousel Events
  CAROUSEL_NAVIGATE: 'carousel_navigate',
  CAROUSEL_SWIPE: 'carousel_swipe',

  // Interaction Events
  TOOLTIP_OPEN: 'tooltip_open',

  // Video Events
  VIDEO_START: 'video_start',
  VIDEO_PROGRESS: 'video_progress',
  VIDEO_PAUSE: 'video_pause',
  VIDEO_EXIT: 'video_exit',

  // Search Events
  SEARCH_QUERY: 'search_query',
  SEARCH_RESULT_CLICK: 'search_result_click',
  SEARCH_OPEN: 'search_open',

  // Share Events
  SHARE_BUTTON_CLICK: 'share_button_click',
  SHARE_ACTION: 'share_action',

  // Social Media Events
  SOCIAL_FOLLOW: 'social_follow',
  SOCIAL_POST_CLICK: 'social_post_click',

  // Form Events
  FORM_SUBMIT: 'form_submit',

  // Scroll Events
  SCROLL: 'scroll',
} as const;

/** Union type of all event trigger values */
export type AnalyticsEventTrigger =
  (typeof ANALYTICS_EVENT_TRIGGERS)[keyof typeof ANALYTICS_EVENT_TRIGGERS];

/** Union type of all event name values */
export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[keyof typeof ANALYTICS_EVENT_NAMES];
